<template>
  <a-modal
    :title="formTitle || '新增菜单'"
    :visible="open"
    @update:visible="(val) => { open = val }"
    @cancel="cancel"
    :width="800"
    :maskClosable="false"
    :keyboard="false"
    :destroyOnClose="true"
    :footer="null"
    class="create-form-modal"
  >
    <a-form
      ref="form"
      :model="form"
      :rules="rules"
      :label-col="{ flex: '0 0 150px' }"
      :wrapper-col="{ flex: '1 1 auto' }"
    >
      <a-form-item label="上级菜单" name="parentId">
        <a-tree-select
          v-model:value="form.parentId"
          style="width: 100%"
          :dropdown-style="{ maxHeight: '400px', overflow: 'auto' }"
          :tree-data="menuOptions"
          placeholder="请选择"
          :field-names="{
            children: 'children',
            label: 'menuName',
            value: 'id',
          }"
          tree-default-expand-all
        >
        </a-tree-select>
      </a-form-item>
      <a-form-item label="菜单类型" name="menuType">
        <a-radio-group v-model:value="form.menuType" button-style="solid">
          <a-radio-button value="M">目录</a-radio-button>
          <a-radio-button value="C">菜单</a-radio-button>
          <a-radio-button value="F">按钮</a-radio-button>
        </a-radio-group>
      </a-form-item>
      <a-form-item label="图标" name="icon" v-if="form.menuType != 'F'">
        <div>
          <a-space size="large">
            <span v-if="form.icon" style="display: inline-flex; align-items: center; font-size: 24px; min-width: 24px; min-height: 24px;">
              <template v-if="isIconPath(form.icon)">
                <img :src="form.icon" alt="icon" style="width: 24px; height: 24px;" />
              </template>
              <template v-else-if="allIcon && allIcon[form.icon + 'Icon'] && isValidComponent(allIcon[form.icon + 'Icon'])">
                <component 
                  :is="allIcon[form.icon + 'Icon']" 
                  :key="`form-icon-${form.icon}`"
                />
              </template>
              <a-icon
                v-else
                :type="form.icon"
              />
            </span>
            <a-button type="dashed" @click="selectIcon" class="select-icon-btn"> 选择图标 </a-button>
            <a
              @click.stop="cancelSelectIcon"
              v-if="iconVisible"
              style="margin-left: 8px; cursor: pointer;"
            >
              收起
              <UpOutlined />
            </a>
          </a-space>
          <div v-if="iconVisible" style="margin-top: 16px;" @click.stop>
            <a-card
              :body-style="{ padding: '10px 20px' }"
              :bordered="false"
            >
              <icon-selector
                :value="form.icon"
                @change="handleIconChange"
                :svgIcons="iconList"
                :allIcon="allIcon"
              />
            </a-card>
          </div>
        </div>
      </a-form-item>
      <a-form-item label="菜单名称" name="menuName">
        <a-input v-model:value="form.menuName" placeholder="请输入" />
      </a-form-item>
      <a-form-item label="排序" name="orderNum">
        <a-input-number
          v-model:value="form.orderNum"
          :min="0"
          :max="9999"
          style="width: 100%"
        />
      </a-form-item>
      <a-form-item name="isFrame" v-if="form.menuType != 'F'">
        <template v-slot:label>
          <span>
            是否外链
            <a-tooltip>
              <template v-slot:title>
                选择是外链则路由地址需要以`http(s)://`开头
              </template>
              <QuestionCircleOutlined />
            </a-tooltip>
          </span>
        </template>
        <a-radio-group v-model:value="form.isFrame" button-style="solid">
          <a-radio-button
            v-for="(d, index) in isFrameOptions"
            :key="index"
            :value="parseInt(d.value)"
            >{{ d.label }}</a-radio-button
          >
        </a-radio-group>
      </a-form-item>
      <a-form-item name="path" v-if="form.menuType != 'F'">
        <template v-slot:label>
          <span>
            路由地址
            <a-tooltip>
              <template v-slot:title>
                访问的路由地址，如：`user`，如外网地址需内链访问则以`http(s)://`开头
              </template>
              <QuestionCircleOutlined />
            </a-tooltip>
          </span>
        </template>
        <a-input v-model:value="form.path" placeholder="请输入" />
      </a-form-item>
      <a-form-item name="component" v-if="form.menuType == 'C'">
        <template v-slot:label>
          <span>
            组件路径
            <a-tooltip>
              <template v-slot:title>
                访问的组件路径，如：`system/user/index`，默认在`views`目录下
              </template>
              <QuestionCircleOutlined />
            </a-tooltip>
          </span>
        </template>
        <a-input v-model:value="form.component" placeholder="请输入" />
      </a-form-item>
      <a-form-item name="perms" v-if="form.menuType != 'M'">
        <template v-slot:label>
          <span>
            权限标识
            <a-tooltip>
              <template v-slot:title>
                控制器中定义的权限字符，如：@PreAuthorize(`@ss.hasPermi('system:user:list')`)
              </template>
              <QuestionCircleOutlined />
            </a-tooltip>
          </span>
        </template>
        <a-input
          v-model:value="form.perms"
          placeholder="请输入"
          :maxLength="100"
        />
      </a-form-item>
      <a-form-item name="visible" v-if="form.menuType != 'F'">
        <template v-slot:label>
          <span>
            是否显示
            <a-tooltip>
              <template v-slot:title>
                选择隐藏则路由将不会出现在侧边栏，但仍然可以访问
              </template>
              <QuestionCircleOutlined />
            </a-tooltip>
          </span>
        </template>
        <a-radio-group v-model:value="form.visible" button-style="solid">
          <a-radio-button
            v-for="(d, index) in visibleOptions"
            :key="index"
            :value="parseInt(d.value)"
            >{{ d.label }}</a-radio-button
          >
        </a-radio-group>
      </a-form-item>
      <a-form-item name="status" v-if="form.menuType != 'F'">
        <template v-slot:label>
          <span>
            状态
            <a-tooltip>
              <template v-slot:title>
                选择停用则路由将不会出现在侧边栏，也不能被访问
              </template>
              <QuestionCircleOutlined />
            </a-tooltip>
          </span>
        </template>
        <a-radio-group v-model:value="form.status" button-style="solid">
          <a-radio-button
            v-for="(d, index) in statusOptions"
            :key="index"
            :value="parseInt(d.value)"
            >{{ d.label }}</a-radio-button
          >
        </a-radio-group>
      </a-form-item>
      <a-form-item name="isCache" v-if="form.menuType == 'C'">
        <template v-slot:label>
          <span>
            是否缓存
            <a-tooltip>
              <template v-slot:title>
                选择是则会被`keep-alive`缓存，需要匹配组件的`name`和地址保持一致，仅在多页签下有效。
              </template>
              <QuestionCircleOutlined />
            </a-tooltip>
          </span>
        </template>
        <a-radio-group v-model:value="form.isCache" button-style="solid">
          <a-radio-button
            v-for="(d, index) in statusOptions"
            :key="index"
            :value="parseInt(d.value)"
            >{{ d.label }}</a-radio-button
          >
        </a-radio-group>
      </a-form-item>
      <div class="bottom-control">
        <a-space>
          <a-button @click="cancel" class="cancel-btn"> 取消 </a-button>
          <a-button type="primary" :loading="submitLoading" @click="submitForm" class="save-btn">
            保存
          </a-button>
        </a-space>
      </div>
    </a-form>
  </a-modal>
</template>

<script>
import { $on, $off, $once, $emit } from '../../../../utils/gogocodeTransfer'

import { getMenu, addMenu, updateMenu } from '@/api/system/menu'
import allIcon from '@/core/icons'
import icons from '@/utils/requireIcons'
import IconSelector from '@/components/IconSelector'
import { UpOutlined, QuestionCircleOutlined } from '@ant-design/icons-vue'

export default {
  name: 'CreateForm',
  props: {
    menuOptions: {
      type: Array,
      required: true,
    },
  },
  components: {
    IconSelector,
    UpOutlined,
    QuestionCircleOutlined,
  },
  data() {
    return {
      visibleOptions: [
        { value: 1, label: '开启' },
        { value: 0, label: '关闭' },
      ],
      statusOptions: [
        { value: 1, label: '开启' },
        { value: 0, label: '关闭' },
      ],
      isFrameOptions: [
        { value: 1, label: '是' },
        { value: 0, label: '否' },
      ],
      isCacheOptions: [
        { value: 1, label: '缓存' },
        { value: 0, label: '不缓存' },
      ],
      allIcon,
      iconVisible: false,
      iconList: icons,
      submitLoading: false,
      formTitle: '',
      // 表单参数
      form: {
        id: undefined,
        parentId: '0',
        menuName: undefined,
        icon: undefined,
        menuType: 'M',
        orderNum: undefined,
        isFrame: 0,
        isCache: 1,
        visible: 1,
        status: 1,
      },
      open: false,
      rules: {
        menuName: [
          { required: true, message: '菜单名称不能为空', trigger: 'blur' },
        ],
        orderNum: [
          { required: true, message: '菜单顺序不能为空', trigger: 'blur' },
        ],
        path: [
          { required: true, message: '路由地址不能为空', trigger: 'blur' },
        ],
        component: [
          { required: true, message: '组件路径不能为空', trigger: 'blur' },
        ],
      },
    }
  },
  created() {},
  computed: {},
  watch: {
    'form.menuType': {
      deep: true,

      // deep: true
      handler(newVal) {
        if (newVal) {
        }
      },
    },
    // 监听 open 状态变化，关闭时清理状态
    open(newVal) {
      if (!newVal) {
        // 弹窗关闭时清理状态
        this.iconVisible = false
        this.$nextTick(() => {
          this.reset()
          // 清理表单验证状态
          if (this.$refs.form) {
            this.$refs.form.clearValidate()
          }
        })
      }
    },
  },
  methods: {
    /** 判断是否是图标文件路径 */
    isIconPath(icon) {
      if (!icon) return false
      // 检查是否是文件路径（以 / 开头或包含 .svg/.png/.jpg 等）
      return typeof icon === 'string' && (icon.startsWith('/') || icon.startsWith('./') || /\.(svg|png|jpg|jpeg|gif|ico)$/i.test(icon))
    },
    /** 判断是否是有效的 Vue 组件 */
    isValidComponent(component) {
      if (!component) return false
      // 如果是函数（组件构造函数）或对象（组件选项对象），则是有效组件
      return typeof component === 'function' || (typeof component === 'object' && component !== null && !Array.isArray(component))
    },
    changeMenutype() {},
    filterIcons() {
      this.iconList = icons
      if (this.name) {
        this.iconList = this.iconList.filter((item) => item.includes(this.name))
      }
    },
    hideIconSelect() {
      this.iconList = icons
      this.iconVisible = false
    },
    // 取消按钮
    cancel() {
      this.open = false
      // watch 会监听 open 的变化并清理状态
      // 触发关闭事件，通知父组件刷新表格
      $emit(this, 'close')
    },
    // 表单重置
    reset() {
      this.form = {
        id: undefined,
        parentId: '0',
        menuName: undefined,
        icon: undefined,
        menuType: 'M',
        orderNum: undefined,
        isFrame: 0,
        isCache: 1,
        visible: 1,
        status: 1,
      }
    },
    /** 新增按钮操作 */
    handleAdd(row) {
      this.reset()
      $emit(this, 'select-tree')
      if (row != null && row.id) {
        this.form.parentId = row.id
      } else {
        this.form.parentId = '0'
      }
      this.open = true
      this.formTitle = '添加菜单'
    },
    /** 修改按钮操作 */
    handleUpdate(row) {
      if (!row || !row.id) {
        this.$message.error('无效的菜单数据', 3)
        return
      }
      this.reset()
      this.open = true
      this.formTitle = '修改菜单'
      $emit(this, 'select-tree')
      const menuId = row.id
      getMenu(menuId).then((response) => {
        // 检查弹窗是否仍然打开，如果已关闭则不更新表单
        if (!this.open) {
          return
        }
        if (response && response.data) {
          this.form = response.data
        } else {
          this.$message.error('获取菜单详情失败：数据为空', 3)
        }
      }).catch((error) => {
        // 检查弹窗是否仍然打开，如果已关闭则不显示错误
        if (!this.open) {
          return
        }
        console.error('获取菜单详情失败:', error)
        this.$message.error('获取菜单详情失败', 3)
      })
    },
    /** 提交按钮 */
    async submitForm() {
      try {
        await this.$refs.form.validate()
        this.submitLoading = true
        if (this.form.id !== undefined) {
          await updateMenu(this.form)
          this.$message.success('修改成功', 3)
          this.open = false
          this.iconVisible = false
          $emit(this, 'ok')
        } else {
          await addMenu(this.form)
          this.$message.success('新增成功', 3)
          this.open = false
          this.iconVisible = false
          $emit(this, 'ok')
        }
      } catch (error) {
        return false
      } finally {
        this.submitLoading = false
      }
    },
    handleIconChange(icon) {
      this.iconVisible = false
      this.form.icon = icon
    },
    changeIcon(type) {
      this.currentSelectedIcon = type
    },
    selectIcon(e) {
      if (e) {
        e.preventDefault()
        e.stopPropagation()
      }
      this.iconVisible = true
    },
    cancelSelectIcon() {
      this.iconVisible = false
    },
  },
  emits: ['select-tree', 'ok', 'close'],
}
</script>

<style scoped>
/* 按钮样式 */
.save-btn {
  background-color: #165DFF !important;
  border-color: #165DFF !important;
  color: #FFFFFF !important;
  border-radius: 4px !important;
}

.save-btn:hover {
  background-color: #4080ff !important;
  border-color: #4080ff !important;
  color: #FFFFFF !important;
}

.save-btn:focus {
  background-color: #165DFF !important;
  border-color: #165DFF !important;
  color: #FFFFFF !important;
}

.save-btn:active {
  background-color: #0E4ACC !important;
  border-color: #0E4ACC !important;
  color: #FFFFFF !important;
}

.cancel-btn {
  background-color: #F3F3F3 !important;
  border-color: #F3F3F3 !important;
  color: #101010 !important;
  border-radius: 4px !important;
}

.cancel-btn:hover {
  background-color: #F3F3F3 !important;
  border-color: #F3F3F3 !important;
  color: #101010 !important;
}

.bottom-control {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}
</style>

<style>
/* Modal body 样式 */
.create-form-modal .ant-modal-body {
  max-height: 70vh;
  overflow-y: auto;
  padding: 20px 16px 60px 16px !important;
}

.create-form-modal .ant-form-item {
  margin-bottom: 16px;
}

.create-form-modal .ant-form-item-label {
  padding-bottom: 6px;
  flex: 0 0 150px !important;
  max-width: 150px !important;
}

.create-form-modal .ant-form-item-label > label {
  width: 100%;
  text-align: right;
  justify-content: flex-end;
}

.create-form-modal .ant-form-item-control {
  flex: 1;
  min-width: 0;
}

/* 输入框样式 - 圆角和边框 */
.create-form-modal .ant-input,
.create-form-modal .ant-input:hover,
.create-form-modal .ant-input:focus {
  border-radius: 4px !important;
  border: 1px solid #C7C7C7 !important;
}

.create-form-modal .ant-input-affix-wrapper,
.create-form-modal .ant-input-affix-wrapper:hover,
.create-form-modal .ant-input-affix-wrapper-focused {
  border-radius: 4px !important;
  border: 1px solid #C7C7C7 !important;
}

/* 带前缀/后缀的输入框内部input去掉边框，避免嵌套 */
.create-form-modal .ant-input-affix-wrapper .ant-input {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

/* 选择框样式 */
.create-form-modal .ant-select-selector,
.create-form-modal .ant-select-selector:hover,
.create-form-modal .ant-select-focused .ant-select-selector {
  border-radius: 4px !important;
  border: 1px solid #C7C7C7 !important;
}

/* 树选择器样式 */
.create-form-modal .ant-select-tree-selector,
.create-form-modal .ant-select-tree-selector:hover,
.create-form-modal .ant-select-tree-focused .ant-select-tree-selector {
  border-radius: 4px !important;
  border: 1px solid #C7C7C7 !important;
}

/* 日期选择器样式 */
.create-form-modal .ant-picker,
.create-form-modal .ant-picker:hover,
.create-form-modal .ant-picker-focused {
  border-radius: 4px !important;
  border: 1px solid #C7C7C7 !important;
}

/* 数字输入框样式 */
.create-form-modal .ant-input-number,
.create-form-modal .ant-input-number:hover,
.create-form-modal .ant-input-number-focused {
  border-radius: 4px !important;
  border: 1px solid #C7C7C7 !important;
}

/* 按钮保持圆角 */
.create-form-modal .ant-btn {
  border-radius: 4px !important;
}

/* 选择图标按钮样式 */
.create-form-modal .select-icon-btn {
  border-radius: 4px !important;
}

/* Radio Button 样式 */
.create-form-modal .ant-radio-button-wrapper {
  border-radius: 4px !important;
}

.create-form-modal .ant-radio-button-wrapper:first-child {
  border-radius: 4px 0 0 4px !important;
}

.create-form-modal .ant-radio-button-wrapper:last-child {
  border-radius: 0 4px 4px 0 !important;
}

.create-form-modal .ant-radio-button-wrapper-checked {
  background-color: #165DFF !important;
  border-color: #165DFF !important;
  color: #FFFFFF !important;
  box-shadow: none !important;
  outline: none !important;
}

.create-form-modal .ant-radio-button-wrapper-checked:hover {
  background-color: #4080ff !important;
  border-color: #4080ff !important;
  color: #FFFFFF !important;
  box-shadow: none !important;
  outline: none !important;
}

.create-form-modal .ant-radio-button-wrapper-checked:focus {
  background-color: #165DFF !important;
  border-color: #165DFF !important;
  color: #FFFFFF !important;
  box-shadow: none !important;
  outline: none !important;
}

.create-form-modal .ant-radio-button-wrapper-checked:focus-within {
  box-shadow: none !important;
  outline: none !important;
}

.create-form-modal .ant-radio-button-wrapper-checked::before {
  background-color: #165DFF !important;
}

.create-form-modal .ant-radio-button-wrapper-checked:active {
  border-color: #165DFF !important;
  box-shadow: none !important;
  outline: none !important;
}
</style>
